<script lang="ts">
  import Question from "../icons/Question.svelte";
  import { Copy, CheckSmall } from "../icons/index.js";
  import { createTooltip, melt } from "@melt-ui/svelte";
  import type { Snippet } from "svelte";
  import type { ClassValue } from "svelte/elements";
  import { scale } from "svelte/transition";

  interface Props {
    text: string;
    position?:
      | "top"
      | "top-start"
      | "top-end"
      | "right"
      | "right-start"
      | "right-end"
      | "bottom"
      | "bottom-start"
      | "bottom-end"
      | "left"
      | "left-start"
      | "left-end";
    hideIcon?: boolean;
    iconOnly?: boolean;
    copyable?: boolean;
    children?: Snippet;
    class?: ClassValue;
    openDelay?: number;
    group?: boolean | string;
    contentClass?: ClassValue;
  }

  let {
    text,
    position = "top",
    hideIcon = false,
    iconOnly = false,
    copyable = false,
    children,
    class: className,
    openDelay = 0,
    group = false,
    contentClass,
  }: Props = $props();

  let showCopySuccess = $state(false);

  function handleCopy() {
    navigator.clipboard.writeText(text);
    showCopySuccess = true;
    setTimeout(() => {
      showCopySuccess = false;
    }, 1000);
  }

  const {
    elements: { trigger, content },
    states: { open },
    // svelte-ignore state_referenced_locally
  } = createTooltip({
    positioning: {
      placement: position,
    },
    group,
    openDelay,
    closeDelay: copyable ? 100 : 0,
    closeOnPointerDown: false,
    forceVisible: true,
  });
</script>

<button
  type="button"
  class={["trigger focus-ring", hideIcon ? "" : "flex items-center", "gap-1.5", className]}
  class:rounded-full={iconOnly}
  {...$trigger} use:$trigger.action
>
  {#if !iconOnly}
    <span>{@render children?.()}</span>
  {/if}

  {#if !hideIcon || iconOnly}
    <Question aria-hidden="true" class="rounded-full text-subtle" />
  {/if}
</button>

{#if $open}
  <div
    {...$content} use:$content.action
    transition:scale={{ duration: 150, opacity: 0, start: 0.8 }}
    class={[
      "origin-bottom rounded-[20px] bg-alpha-700 px-6 py-4 text-[white] shadow-popover backdrop-blur-xs",
      copyable ? "max-w-none" : "max-w-[224px]",
      contentClass || "z-10",
    ]}
  >
    {#if copyable}
      <div class="flex items-center gap-2">
        <p class="font-mono text-xs whitespace-nowrap">{text}</p>
        <button
          onclick={handleCopy}
          aria-label="Copy to clipboard"
          type="button"
          class="shrink-0 rounded-xs text-mono-100/60 transition-colors duration-100 hover:text-mono-100"
        >
          {#if showCopySuccess}
            <CheckSmall aria-hidden="true" />
          {:else}
            <Copy aria-hidden="true" />
          {/if}
        </button>
      </div>
    {:else}
      <p class="text-sm">{text}</p>
    {/if}
  </div>
{/if}
